<template>
	<div
		class="tab-wrapper"
		:class="{
			'tab-wrapper--embedded': embedded
		}"
	>
		<component
			:is="sectionComponent"
			:title="embedded ? null : $t('orders-view.new-order.title.delivery-info')"
		>
			<BaseTextField
				v-model="deliveryName"
				:disabled="disabled"
				name="name"
				type="text"
				:label-text="`${$t('common.fields.name')} / ${$t('common.fields.company-name')}`"
			/>
			<AutoCompleteField
				v-model="deliveryAddress"
				v-validate="'required'"
				:disabled="disabled"
				:helper-text="errors.first('deliveryAddress') ? errors.first('deliveryAddress') : ''"
				:invalid="errors.has('deliveryAddress')"
				name="deliveryAddress"
				type="text"
				:label-text="$t('common.fields.address')"
				@place-changed="handlePlaceChange"
			/>
			<InputGroup>
				<BaseDropdownInput
					v-model="deliveryCountry"
					v-validate:deliveryCountry="'required'"
					:disabled="disabled"
					:list="countries"
					:helper-text="errors.first('deliveryCountry')"
					:invalid="errors.has('deliveryCountry')"
					:multiple="true"
					name="deliveryCountry"
					:label-text="$t('common.fields.country')"
				/>
				<BaseTextField
					v-model="deliveryPostalCode"
					v-validate="'required'"
					:disabled="disabled"
					:helper-text="errors.first('deliveryPostalCode')"
					:invalid="errors.has('deliveryPostalCode')"
					name="deliveryPostalCode"
					multiple="true"
					type="text"
					:label-text="$t('common.fields.postal-code')"
				/>
			</InputGroup>
			<AutoCompleteField
				v-model="deliveryCity"
				v-validate="'required'"
				:disabled="disabled"
				:helper-text="errors.first('deliveryCity')"
				:invalid="errors.has('deliveryCity')"
				name="deliveryCity"
				type="text"
				types="(regions)"
				:label-text="$t('common.fields.city')"
				:country="deliveryCountry.id || null"
			/>
			<BaseTextField
				v-model="deliveryEmail"
				:disabled="disabled"
				name="email"
				type="email"
				:label-text="$t('common.fields.email')"
			/>
			<PhoneNumberField
				ref="phoneNumber"
				v-model="deliveryPhoneNumber"
				:disabled="disabled"
				required
				:default-country="deliveryCountry?.id || defaultCountry"
				:label-text="$t('common.fields.phone')"
				:helper-text="errors.first('phoneNumber')"
				:invalid="errors.has('phoneNumber')"
				@valid="errors.remove('phoneNumber')"
			/>
		</component>
	</div>
</template>

<script>

import {  mapMutations, mapGetters } from '@/store/modules/Clients/helpers'
import { UPDATE_CLIENT_FORM } from '@/store/modules/Clients/mutation-types'
import AutoCompleteField from '@/components/Essentials/AutoCompleteField'
import FormSection from '@/components/Essentials/FormSection'
import PhoneNumberField from '@/components/Essentials/PhoneNumberField'
import { getCityFromPlace, getCountryComponentFromPlace } from '@/utils/address'

export default {
	name: 'ClientInfo',
	components: {
		AutoCompleteField,
		FormSection,
		PhoneNumberField
	},
	props: {
		countries: {
			type: Array,
			default: () => []
		},
		embedded: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			deliveryCountry: {
				name: null,
				id: null
			},
			deliveryName: null,
			deliveryAddress: null,
			deliveryCity: null,
			deliveryPostalCode: null,
			deliveryEmail: null,
			deliveryPhoneNumber: null
		}
	},
	computed: {
		...mapGetters({
			editClientForm: 'editClientForm'
		}),

		defaultCountry () {
			if (this.$i18n.locale === 'en') {
				return 'gb'
			}
			return this.$i18n.locale
		},

		sectionComponent () {
			return this.embedded ? 'div' : FormSection
		}
	},
	watch: {
	},
	created () {
		this.$validator.pause()

		const country = this.editClientForm.deliveryCountry ? this.editClientForm.deliveryCountry : this.editClientForm.country ? this.editClientForm.country : this.editClientForm.companyCountry ? this.editClientForm.companyCountry : 'LT'

		this.deliveryCountry = this.countries.find(_country => country.toLowerCase() === _country.id.toLowerCase() )

		this.deliveryName = this.editClientForm.deliveryName || this.editClientForm.name && this.editClientForm.surname ? `${this.editClientForm.name} ${this.editClientForm.surname}` : this.editClientForm.name || this.editClientForm.surname || ''
		this.deliveryPhoneNumber = this.editClientForm.deliveryPhoneNumber || this.editClientForm.phoneNumber
		this.deliveryEmail = this.editClientForm.deliveryEmail || this.editClientForm.email
		this.deliveryPostalCode = this.editClientForm.deliveryPostalCode || this.editClientForm.postalCode
		this.deliveryAddress = this.editClientForm.deliveryAddress || this.editClientForm.address
		this.deliveryCity = this.editClientForm.deliveryCity || this.editClientForm.city

		const city = this.editClientForm.deliveryCity ? this.editClientForm.deliveryCity : this.editClientForm.companyCity ? this.editClientForm.companyCity : null

		this.deliveryCity = city
	},
	methods: {
		...mapMutations({
			updateClientForm: UPDATE_CLIENT_FORM
		}),
		async validate (locked = false) {
			this.updateClientForm({
				deliveryName: this.deliveryName,
				deliveryAddress: this.deliveryAddress,
				deliveryCountry: this.deliveryCountry?.id || null,
				deliveryPostalCode: this.deliveryPostalCode,
				deliveryCity: this.deliveryCity,
				deliveryEmail: this.deliveryEmail,
				deliveryPhoneNumber: this.deliveryPhoneNumber
			})
			if (locked) {
				return true
			}

			this.$validator.resume()
			const valid = await this.$validator.validateAll()

			if (!this.$refs.phoneNumber.isValid()) {
				this.errors.add({ field: 'phoneNumber', msg: this.$t('orders-view.inner.delivery-info.delivery-info-modal.errors.incorrect-phone-number') })
				this.$emit('shake')
				return false
			} else {
				this.errors.remove('phoneNumber')
			}

			if (valid) {
				return true
			}

			this.$emit('shake')
			return false
		},
		handlePlaceChange ({ _addressData, placeResultData }) {
			const country = getCountryComponentFromPlace(placeResultData)
			const city = getCityFromPlace(placeResultData)
			const postalCode = placeResultData.address_components?.find(c => c.types.includes('postal_code'))

			this.deliveryCountry = this.countries.find(c => c.id === country?.short_name) || {
				id: null,
				name: null
			}
			this.deliveryCity = city
			this.deliveryPostalCode = postalCode ? postalCode.short_name : null

		}
	}
}
</script>

<style lang="scss">
	.tab-wrapper {
		display: flex;
		flex-direction: column;

		&--embedded {
			width: 100%;
		}
	}
</style>
